import { escapeHtml } from "../router.js";

/**
 * Lazy PDF viewer built on pdfjs-dist. The library is imported only when `mountPdfViewer` runs,
 * so the main bundle stays small. Pages render on scroll (IntersectionObserver).
 *
 * texts: TextOut[] from the proposition detail. Returns the container markup; call
 * `mountPdfViewer(container, texts)` after inserting it into the DOM.
 */
export function renderPdfSection(texts) {
  if (!texts?.length) {
    return `<p class="muted">Texto integral não disponível.</p>`;
  }
  const primary = texts.find((t) => t.is_primary) || texts[0];
  const options = texts
    .map(
      (t) =>
        `<option value="${t.id}" ${t.id === primary.id ? "selected" : ""}>${escapeHtml(textKindLabel(t))}${
          t.page_count ? ` · ${t.page_count} pág.` : ""
        }</option>`,
    )
    .join("");
  return `
    <div class="pdfbox" data-pdf-box>
      <div class="pdfbox__bar">
        ${texts.length > 1 ? `<label class="pdfbox__select">Documento <select data-pdf-select>${options}</select></label>` : `<span class="meta-badge">${escapeHtml(textKindLabel(primary))}</span>`}
        <a class="card__link" data-pdf-original href="${escapeHtml(primary.original_url)}" target="_blank" rel="noopener">Abrir PDF original ↗</a>
      </div>
      <div class="pdfbox__status muted" data-pdf-status>Carregando visualizador…</div>
      <div class="pdfbox__pages" data-pdf-pages></div>
    </div>`;
}

export function textKindLabel(t) {
  const map = {
    inteiro_teor: "Inteiro teor",
    avulso_inicial: "Avulso inicial",
    substitutivo: "Substitutivo",
    redacao_final: "Redação final",
    parecer: "Parecer",
    emenda: "Emenda",
    other: "Documento",
  };
  return t.label || map[t.kind] || t.kind || "Documento";
}

export async function mountPdfViewer(container, texts) {
  const box = container.querySelector("[data-pdf-box]");
  if (!box || !texts?.length) return;
  const status = box.querySelector("[data-pdf-status]");
  const pagesEl = box.querySelector("[data-pdf-pages]");
  const select = box.querySelector("[data-pdf-select]");
  const original = box.querySelector("[data-pdf-original]");
  let pdfjs;
  try {
    const [lib, worker] = await Promise.all([import("pdfjs-dist"), import("pdfjs-dist/build/pdf.worker.min.mjs?url")]);
    pdfjs = lib;
    pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  } catch (err) {
    console.error(err);
    status.innerHTML = `<span class="banner banner--error">Não foi possível carregar o visualizador. Use o link para o PDF original.</span>`;
    return;
  }
  let currentDoc = null;
  let observer = null;

  async function load(text) {
    if (observer) observer.disconnect();
    if (currentDoc) {
      try {
        await currentDoc.destroy();
      } catch {
        /* ignore */
      }
      currentDoc = null;
    }
    pagesEl.innerHTML = "";
    original.href = text.original_url;
    status.textContent = "Carregando PDF…";
    let doc;
    try {
      doc = await pdfjs.getDocument({ url: text.proxy_url, withCredentials: false }).promise;
    } catch (err) {
      console.error(err);
      status.innerHTML = `<span class="banner banner--error">Falha ao carregar o PDF (${escapeHtml(err?.message || err)}). Tente o PDF original.</span>`;
      return;
    }
    currentDoc = doc;
    status.textContent = `${doc.numPages} página${doc.numPages === 1 ? "" : "s"}`;
    const width = Math.min(pagesEl.clientWidth || 720, 900);
    const first = await doc.getPage(1);
    const baseViewport = first.getViewport({ scale: 1 });
    const scale = width / baseViewport.width;
    const rendered = new Set();
    for (let i = 1; i <= doc.numPages; i++) {
      const holder = document.createElement("div");
      holder.className = "pdfpage";
      holder.dataset.page = String(i);
      holder.style.height = `${Math.round(baseViewport.height * scale)}px`;
      holder.innerHTML = `<span class="pdfpage__num">${i}</span>`;
      pagesEl.appendChild(holder);
    }
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const n = Number(entry.target.dataset.page);
          if (rendered.has(n)) continue;
          rendered.add(n);
          renderPage(doc, n, entry.target, scale);
        }
      },
      { root: null, rootMargin: "600px 0px" },
    );
    pagesEl.querySelectorAll(".pdfpage").forEach((el) => observer.observe(el));
  }

  async function renderPage(doc, n, holder, scale) {
    try {
      const page = await doc.getPage(n);
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement("canvas");
      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;
      const ctx = canvas.getContext("2d");
      await page.render({ canvasContext: ctx, viewport, transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : null }).promise;
      holder.style.height = "auto";
      holder.appendChild(canvas);
    } catch (err) {
      console.error(err);
      holder.innerHTML += `<span class="muted">Falha ao renderizar a página ${n}.</span>`;
    }
  }

  if (select) {
    select.addEventListener("change", () => {
      const t = texts.find((x) => String(x.id) === select.value);
      if (t) load(t);
    });
  }
  const initial = select ? texts.find((x) => String(x.id) === select.value) : texts.find((t) => t.is_primary) || texts[0];
  await load(initial);
}
